<template>
  <div>
    <base-cell
      class="position-relative mb-3 base-cell--clickable"
    >
      <div
        v-if="(!sent && !scheduled && !isTemplate) || (canManageTemplates && isTemplate)"
        class="delete-button__wrapper clickable"
        @click.stop="onClickDeleteBtn"
      >
        <base-icon
          icon="close-x"
          class="delete-button"
          :class="{ 'delete-button--ipad': isIPad }"
        />
      </div>
      <div
        class="entry__thumbnail clickable"
        :class="browserClass"
        @click="onCellClicked"
      >
        <div
          v-if="backgroundImage"
          class="entry__thumbnail__image"
          :style="{'background-image': `url(${backgroundImage})`}"
        />
        <img
          v-else
          class="entry__thumbnail__image"
          :src="require('@/assets/empty-edit-placeholder.svg').default"
        >
      </div>

      <div class="clickable subject__wrapper" @click="onCellClicked">
        <base-badge
          v-if="currentExploreFilter !== 0"
          :type="badgeType"
        >
          {{ badgeText }}
        </base-badge>
        <base-text class="subject">
          {{ subjectLine | striphtml | truncate }}
        </base-text>
        <base-text
          v-if="sent && !!getRecipients && currentExploreFilter !== 0 && currentExploreFilter !== 1"
          class="created"
          light
          style="color: #646464"
        >
          <span>Sent to: </span>
          <span class="text-capitalize">{{ getRecipients }}</span>
        </base-text>
        <base-text
          v-if="isExploreEditsMenu && currentExploreFilter !== 1"
          class="created"
          light
          style="color: #646464"
        >
          <span>Created By:</span>
          <span class="text-capitalize"> {{ ownerFirstName }}</span>
          <span v-if="ownerLastName" class="text-capitalize">, {{ ownerLastName[0] }}</span>
        </base-text>

        <base-text
          class="created"
          light
          style="color: #646464"
        >
          {{ !sent || currentExploreFilter ===1 ? "Created " : "" }}
          {{ $moment(sent ? sentDate : created).format(dateformat) }}
        </base-text>
        <base-text
          v-if="modified && !sent"
          class="created"
          light
          style="color: #646464"
        >
          Last edited {{ $moment(modified).format(dateformat) }}
        </base-text>
        <div v-if="isWishlistRoute">
          <like-button :likes-count="likesCount" :is-liked-by-me="isLikedByMe" :edit-id="pk"></like-button>
        </div>
      </div>
      <div
        ref="cell__buttons"
        class="cell__buttons text-center ml-auto"
        :class="{'cell__buttons--ipad': isIPad}"
      >
        <div
          v-if="canManageTemplates && !isTemplate && !scheduled"
          class="cell__button py-1 mb-2"
          :class="{ 'disable-clicks': isSavingAsTemplate }"
          @click.stop.prevent="saveAsTemplate"
        >
          {{ templateButtonText }}
        </div>
        <div
          v-if="scheduled"
          class="cell__button py-1 mb-2"
          @click.stop.prevent="cancelSchedule"
        >
          Cancel Send
        </div>
        <div
          class="cell__button py-1"
          :class="{ 'disable-clicks': isCloning, 'cell__button--template': isTemplate }"
          @click.stop.prevent="cloneItem(identifier)"
        >
          <div>
            {{ isCloning ? 'Duplicating...' : 'Duplicate' }}
          </div>
        </div>
      </div>
    </base-cell>
  </div>
</template>

<script>

import { EventBus } from '@/event-bus';
import LikeButton from '@/components/LikeButton';
import whichbrowser from '../../mixins/whichbrowser';

const DRAFT = 'draft';
const SCHEDULED = 'scheduled';

export default {
  name: 'DraftCell',
  components: { LikeButton },
  mixins: [whichbrowser],
  props: {
    isMine: {
      type: Boolean,
      required: true,
    },
    isTemplate: {
      type: Boolean,
      required: true,
    },
    subject: {
      type: String,
      required: true,
    },
    backgroundImage: {
      type: String,
      required: true,
    },
    created: {
      type: String,
      required: true,
    },
    identifier: {
      type: Number,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
    modified: {
      type: String,
      required: false,
      default: '',
    },
    sentDate: {
      type: String,
      required: false,
      default: '',
    },

    ownerFirstName: {
      type: String,
      required: false,
      default: '',
    },

    ownerLastName: {
      type: String,
      required: false,
      default: '',
    },

    likesCount: {
      type: Number,
      required: false,
      default: 0,
    },

    isLikedByMe: {
      type: Boolean,
      required: false,
      default: false,
    },

    pk: {
      type: Number,
      required: true,
      default: null,
    },

    isExploreEditsMenu: {
      type: Boolean,
      required: false,
      default: false,
    },
    currentExploreFilter: {
      type: Number,
      required: false,
      default: -1,
    },
    sentTo: {
      type: Object,
      required: false,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: {},
    },
  },
  data() {
    return {
      statusLabels: {
        draft: 'Draft',
        email: 'Email',
        html: 'Share Link',
        wechat: 'WeChat',
        pdf: 'PDF',
        embed: 'Embed Code',
        scheduled: 'Scheduled',
      },
      hasTemplateLibrary: this.useFeature('has_template_library'),
      isWishlistBtnClicked: false,
      recipients: [],
    };
  },
  computed: {
    badgeType() {
      if (this.isTemplate || this.status === 'draft') return 'orange';
      if (this.status === 'scheduled') return 'gray';
      return 'green';
    },
    badgeText() {
      if (this.isTemplate) {
        // Not sure if this should be UPPERCASE or Titlecase
        // https://helloseer.slack.com/archives/C017BN75RFD/p1623852707007400
        return 'Template';
      }
      return this.statusLabels[this.status];
    },
    title() {
      if (this.subject) {
        return this.subject.striphtml();
      }
      return 'Untitled';
    },
    isCloning() {
      return this.$store.state.entries.cloningItems.includes(this.identifier);
    },
    isSavingAsTemplate() {
      return this.$store.state.entries.savingAsTemplateItems.includes(this.identifier);
    },
    sent() {
      return this.status !== DRAFT && this.status !== SCHEDULED;
    },
    scheduled() {
      return this.status === SCHEDULED;
    },
    canManageTemplates() {
      return this.hasTemplateLibrary && this.$store.getters['account/canMakeTemplate'];
    },
    templateButtonText() {
      if (this.isSavingAsTemplate) {
        if (this.$store.state.account.user.profile.institution.mixpanel_enable) {
          this.$mixpanel.track('save_template', {
            account_id: this.$store.state.account.user.user.email,
          });
        }
        return 'Saving...';
      }
      return 'Save Template';
    },
    subjectLine() {
      return this.subject || 'Untitled';
    },
    isWishlistRoute() {
      const queryString = window.location.search;
      const parameters = new URLSearchParams(queryString);
      const routeKey = parameters.get('filter');

      if (routeKey === 'byteam') {
        if (this.$store.state.account.user.profile.institution.mixpanel_enable) {
          this.$mixpanel.track('library_toggle', {
            account_id: this.$store.state.account.user.user.email,
          });
        }
      }
      return routeKey === 'byteam';
    },
    getRecipients() {
      return this.sentTo?.display ? this.sentTo.display !== 'No clients' ? this.sentTo.display : '' : '';
    },
  },
  methods: {
    cloneItem(id) {
      if (!this.isCloning) {
        this.$mixpanel.track('duplicate_edit', {
          account_id: this.$store.state.account.user.user.email,
          edit_id: id,
        });
        this.$store.dispatch('entries/cloneItem', id).then((result) => {
          this.$emit('cloned', result.data.pk);
          if (this.$store.state.account.user.profile.institution.mixpanel_enable) {
            if (this.hasTemplateLibrary) {
              this.$mixpanel.track('Duplicate_Library', {
                account_id: this.$store.state.account.user.user.email,
                entries_item: id,
              });
            }
            this.$mixpanel.track('Duplicate_Edit', {
              account_id: this.$store.state.account.user.user.email,
              entries_item: id,
            });
          }
        });
      }
      if (this.$store.state.account.user.profile.institution.mixpanel_enable) {
        if (this.hasTemplateLibrary) {
          this.$mixpanel.track('Duplicate_Library', {
            account_id: this.$store.state.account.user.user.email,
            entries_item: id,
          });
        }
        this.$mixpanel.track('Duplicate_Edit', {
          account_id: this.$store.state.account.user.user.email,
          entries_item: id,
        });
      }
    },
    openConfirmDelete() {
      EventBus.$emit('confirm', {
        title: 'Are you sure you want to delete?',
        positiveText: 'Delete',
        callback: (confirm) => {
          if (confirm) {
            this.deleteItem(this.identifier);
          }
        },
      });
    },
    deleteItem(id) {
      this.$store.dispatch('entries/deleteItem', id);
    },

    likeItem() {
      this.$store.dispatch('entries/likeItem', this.pk);
    },

    unlikeItem() {
      this.$store.dispatch('entries/unlikeItem', this.pk);
    },
    onCellClicked() {
      if (this.isTemplate) {
        /* Need to create a preview page */
        return this.$router.push(`/entries/${this.identifier}/preview`);
      }
      if (this.currentExploreFilter === 0) {
        // This case is the Library
        return this.$router.push(`/entries/${this.identifier}/library`);
      }
      if (this.sent || this.scheduled) {
        return this.$router.push({ name: 'SentDraftSummary', params: { id: this.identifier, intro: (this.isMine && !this.isExploreEditsMenu) } });
      }
      if (this.$store.state.account.user.profile.institution.mixpanel_enable) {
        this.$mixpanel.track('Use_Template', {
          account_id: this.$store.state.account.user.user.email,
          entries_pk: this.identifier,
        });
      }
      return this.$router.push(`/entries/${this.identifier}/edit`);
    },
    getVerb() {
      if (this.status === 'draft') {
        return 'Created';
      }
      if (this.status === 'email') {
        return 'Sent';
      }
      return 'Shared';
    },
    removeTemplate() {
      this.$store.dispatch('entries/changeTemplateStatus', { id: this.identifier, isTemplate: false })
        .then(() => {
          this.$emit('template-change');
        });
    },
    openConfirmRemoveTemplate() {
      EventBus.$emit('confirm', {
        title: 'Are you sure you want to delete?',
        positiveText: 'Delete',
        callback: (confirm) => {
          if (confirm) {
            this.removeTemplate();
          }
        },
      });
    },
    saveAsTemplate() {
      if (!this.isSavingAsTemplate) {
        this.$store.dispatch('entries/cloneAsTemplate', this.identifier).then(() => {
          EventBus.$emit('confirm', {
            title: 'Done!',
            message: `A copy of ${this.title} has been saved as a template.`,
            showOptions: false,
          });
          if (this.$store.state.account.user.profile.institution.mixpanel_enable) {
            this.$mixpanel.track('Save_Template', {
              account_id: this.$store.state.account.user.user.email,
              template_title: this.title,
            });
          }
        });
      }
    },
    cancelSchedule() {
      this.$emit('unschedule');
    },
    onClickDeleteBtn() {
      if (this.isTemplate) this.openConfirmRemoveTemplate();
      else this.openConfirmDelete();
    },
    onClickWishlistBtn() {
      if (this.isLikedByMe) {
        this.unlikeItem();
      } else {
        this.likeItem();
      }

      // this.isWishlistBtnClicked = !this.isWishlistBtnClicked;
    },
  },
};
</script>

<style lang="scss" scoped>
  .base-cell {
    padding: 16px;

    &--clickable {
      cursor: unset;
    }
    padding: 16px;

    &::v-deep .cell__buttons {
      opacity: 0;
      transition: 0.3s;
    }

    &::v-deep .cell__button {
      transition: 0.3s;
      cursor: pointer;

      &:hover {
        color: #646464;
        border-color: #646464;
      }
    }
    &::v-deep .subject {
      font-family: $font-family-sans-serif-medium;
      font-size: 16px;
      line-height: 18px;
      margin: 5px 0;
      letter-spacing: 0;
      word-break: break-word;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-right: 8px;
      &__wrapper {
        display: grid;
      }
    }
    &::v-deep .created {
      font-size: 12px;
      line-height: 16px;
    }
    &::v-deep .cell__button {
      font-size: 11px;
      line-height: 13px;
      letter-spacing: 0.2px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    & .cell__buttons--ipad{
      opacity: 1;
    }
    &:hover .cell__buttons{
      opacity: 1;
    }
    &:hover .delete-button__wrapper {
      opacity: 1;
    }
  }

.cell {
  &__buttons {
    color: #aaaaaa;
  }

  &__button {
    text-align: center;
    font-size: 11px;
    width: 98px;
    height: 26px;
    border: solid 1px #aaaaaa80;
    &--template {
      width: 98px;
    }
  }
}

.entry__thumbnail {
  background-color: #FFFFFF;
  display: inline-block;
  min-width: 246px;
  height: 200px;
  margin: 0 8px 0 0;
  overflow: hidden;

  @media (min-width: map-get($breakpoints,'md')) {
    margin: 0 24px 0 0;
  }

  &.is-chrome {
    border: none;
  }

  &__image {
    width: 246px;
    height: 200px;
    overflow: hidden;
    background-size: cover;
    background-position: center;
    display: block;
  }
}

.wishlist-main {
  display: flex;
  gap: 5.34px;
  margin-top: 11.5px;
}

$icon-size: 16px;

.delete-button {
  width: $icon-size;
  height: $icon-size;
  align-items: center;
  justify-content: center;
  object-fit: none;

  &--ipad {
    right: 0;
    opacity: 1;
  }

  &__wrapper {
    display: flex;
    opacity: 0;
    transition: opacity 0.3s;
    position: absolute;
    top: $icon-size;
    right: 20px;
    z-index: 19;
    height: $icon-size;
    width: $icon-size;
  }

}

.disable-clicks {
  cursor: wait;
}

.badge {
  font-size: 12px;
  line-height: 130%;
  font-family: $font-family-sans-serif-medium;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding-inline: 0 !important;
  &--sent {
    color: $strong-green;
  }
  &--draft {
    color: $bright-orange;
  }
  &--scheduled {
    color: $gray;
  }
}

@media (max-width: map-get($breakpoints,'lg')) {
  .entry__thumbnail {
    min-width: 167px;
    height: 136px;

    &__image {
      width: 167px;
      height: 136px;
    }
  }
}
</style>
